import React from 'react';

/**
 * "How it got here" — the progression, most recent on the right.
 *
 * Deliberately NOT the old timeline: no years, no company-formation beat.
 * The through-line is organic — eating it, sharing it, then sharing more of
 * it safely — so the steps are stages, not dates.
 *
 * Visual language is lifted from the macro triangle (MacroBalanced): the
 * same #54637A rule, #8b93a0 dots and #95A0AE captions, and the same device
 * of a single green #5DBE3A dot in a cream ring — here marking where we
 * currently are. The final step is drawn hollow because it hasn't happened yet.
 */

interface Stage {
  title: string;
  desc: string;
  /** The green, cream-ringed node — where we are now. */
  mark?: boolean;
  /** Drawn hollow: ahead of us, not behind. */
  ahead?: boolean;
}

const STAGES: Stage[] = [
  {
    title: 'Cooking with intention',
    desc: 'A yoga resort kitchen, cooking around allergens, vegetarian and vegan diets.',
  },
  {
    title: 'Taking it home',
    desc: 'The habit comes home. The cooking carries on in a home kitchen.',
  },
  {
    title: 'Eating it for years',
    desc: 'A long stretch living on the early version of TrueNooch, cooked for ourselves.',
  },
  {
    title: 'Sharing with friends',
    desc: 'It leaves the house, because people keep asking for it.',
  },
  {
    title: 'Dreaming bigger',
    desc: 'TrueNooch forms here.',
  },
  {
    title: 'Friendly distribution',
    desc: 'Sharing with more people.',
    mark: true,
  },
  {
    title: 'TrueNooch cookbook',
    desc: 'Education around macro-balanced vegan cooking.',
    ahead: true,
  },
];

export function StoryPath() {
  return (
    <section className="story-path fade-in">
      <div className="story-wrap">
        <p className="story-kicker">How it got here</p>
        <h2>Eating it, sharing it, sharing more of it</h2>
        <p className="story-intro">
          No boardroom, no launch date. TrueNooch got here the way a recipe does — cooked, eaten,
          handed to a friend, then made properly enough to hand to more of them.
        </p>

        <ol className="story-path-track">
          {STAGES.map((s) => (
            <li
              key={s.title}
              className={[
                'story-path-step',
                s.mark ? 'story-path-step-mark' : '',
                s.ahead ? 'story-path-step-ahead' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="story-path-dot" aria-hidden="true" />
              <p className="story-path-title">{s.title}</p>
              <p className="story-path-desc">{s.desc}</p>
            </li>
          ))}
        </ol>

      </div>
    </section>
  );
}
